import { c as _c } from "react/compiler-runtime";
import { IcoClose24 } from '@onefootprint/icons';
import * as RadixDialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { cx } from 'class-variance-authority';
import type React from 'react';
import { type JSX, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createFilter } from 'react-select';
import IconButton from '../../../../icon-button';
import Overlay from '../../../../overlay';
import SearchInput from '../../../../search-input';
import Text from '../../../../text';
import type { BaseSelectOption } from '../../base-select.types';
import type { ItemProps } from './components/item';
import Item from './components/item';
type PickerProps = {
  open: boolean;
  onClose: () => void;
  height: number;
  id?: string;
  placeholder?: string;
  options?: BaseSelectOption[];
  value?: BaseSelectOption;
  renderEmptyState: () => JSX.Element;
  onChange: (newValue: BaseSelectOption) => void;
  OptionComponent?: React.ComponentType<ItemProps>;
};

// The same match the desktop select uses: it ignores case and accents, so "mexico" finds "México".
const matchesSearch = createFilter<BaseSelectOption>();
const Picker = t0 => {
  const $ = _c(64);
  const {
    open,
    onClose,
    height,
    id,
    placeholder,
    value,
    options,
    renderEmptyState,
    onChange,
    OptionComponent: t1
  } = t0;
  const OptionComponent = t1 === undefined ? Item : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "components.picker"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation("ui", t2);
  const [search, setSearch] = useState("");
  const [hasScroll, setHasScroll] = useState(false);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = e => {
      const target = e.target as HTMLElement;
      setHasScroll(target.scrollTop > 0);
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const handleScroll = t3;
  let t4;
  bb0: {
    if (!search) {
      t4 = options;
      break bb0;
    }
    let t5;
    if ($[2] !== options || $[3] !== search) {
      t5 = options?.filter(option => matchesSearch({
        label: option.label,
        value: option.value,
        data: option
      }, search));
      $[2] = options;
      $[3] = search;
      $[4] = t5;
    } else {
      t5 = $[4];
    }
    t4 = t5;
  }
  const filteredOptions = t4;
  let t5;
  let t6;
  if ($[5] !== open) {
    t5 = () => {
      if (!open) {
        setSearch("");
      }
    };
    t6 = [open];
    $[5] = open;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  let t7;
  if ($[8] !== onClose) {
    t7 = () => {
      onClose();
    };
    $[8] = onClose;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const handleClose = t7;
  let t8;
  if ($[10] !== open) {
    t8 = <RadixDialog.Overlay asChild={true}><Overlay isVisible={open} /></RadixDialog.Overlay>;
    $[10] = open;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = cx("fixed left-0 bottom-0 w-full bg-primary rounded-t-2xl z-[50]", "animate-[bottomSheetIn_150ms_cubic-bezier(0.16,1,0.3,1)]", "data-[state=closed]:animate-[bottomSheetOut_150ms_cubic-bezier(0.16,1,0.3,1)]");
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const t10 = `${height}px`;
  let t11;
  if ($[13] !== t10) {
    t11 = {
      height: t10
    };
    $[13] = t10;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== handleClose) {
    t12 = <RadixDialog.Close asChild={true}><div className="flex items-center justify-center absolute top-2 right-2"><IconButton aria-label="close" variant="ghost" onClick={handleClose} icon={IcoClose24} /></div></RadixDialog.Close>;
    $[15] = handleClose;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== t) {
    t13 = t(_temp);
    $[17] = t;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t13) {
    t14 = <Text variant="label-2">{t13}</Text>;
    $[19] = t13;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== t) {
    t15 = t(_temp2);
    $[21] = t;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== t15) {
    t16 = <RadixDialog.Title asChild={true}><VisuallyHidden>{t15}</VisuallyHidden></RadixDialog.Title>;
    $[23] = t15;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== t12 || $[26] !== t14 || $[27] !== t16) {
    t17 = <div className="h-10 pt-4 flex justify-center items-center relative">{t12}{t14}{t16}</div>;
    $[25] = t12;
    $[26] = t14;
    $[27] = t16;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== hasScroll) {
    t18 = cx("pb-4", {
      "border-b border-tertiary": hasScroll
    });
    $[29] = hasScroll;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setSearch("");
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== id || $[33] !== placeholder || $[34] !== search) {
    t20 = <SearchInput placeholder={placeholder} id={id} onChangeText={setSearch} onReset={t19} tabIndex={0} value={search} data-dd-privacy="mask" className="min-h-10" />;
    $[32] = id;
    $[33] = placeholder;
    $[34] = search;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== t18 || $[37] !== t20) {
    t21 = <div className={t18}>{t20}</div>;
    $[36] = t18;
    $[37] = t20;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  const t22 = `${height - 100}px`;
  let t23;
  if ($[39] !== t22) {
    t23 = {
      maxHeight: t22
    };
    $[39] = t22;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== OptionComponent || $[42] !== filteredOptions || $[43] !== onChange || $[44] !== renderEmptyState || $[45] !== value) {
    t24 = filteredOptions?.length ? filteredOptions.map(option_0 => <OptionComponent key={option_0.value} option={option_0} value={value} onSelect={() => onChange(option_0)} />) : renderEmptyState();
    $[41] = OptionComponent;
    $[42] = filteredOptions;
    $[43] = onChange;
    $[44] = renderEmptyState;
    $[45] = value;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] !== t23 || $[48] !== t24) {
    t25 = <div className="overflow-y-auto" style={t23} onScroll={handleScroll}>{t24}</div>;
    $[47] = t23;
    $[48] = t24;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== t21 || $[51] !== t25) {
    t26 = <div className="p-4">{t21}{t25}</div>;
    $[50] = t21;
    $[51] = t25;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] !== t11 || $[54] !== t17 || $[55] !== t26) {
    t27 = <RadixDialog.Content aria-describedby={undefined} asChild={true}><div className={t9} style={t11}>{t17}{t26}</div></RadixDialog.Content>;
    $[53] = t11;
    $[54] = t17;
    $[55] = t26;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  let t28;
  if ($[57] !== t27 || $[58] !== t8) {
    t28 = <RadixDialog.Portal>{t8}{t27}</RadixDialog.Portal>;
    $[57] = t27;
    $[58] = t8;
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  let t29;
  if ($[60] !== handleClose || $[61] !== open || $[62] !== t28) {
    t29 = <RadixDialog.Root open={open} onOpenChange={handleClose}>{t28}</RadixDialog.Root>;
    $[60] = handleClose;
    $[61] = open;
    $[62] = t28;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  return t29;
};
export default Picker;
function _temp($) {
  return $["placeholder-default"];
}
function _temp2($_0) {
  return $_0["aria-label-default"];
}